// ConceptDisplay.js
// Ce module gère l'affichage des concepts dans différents formats

import React from 'react';
import { BookOpen, FileText, Tag, Network, PieChart, Loader2 } from 'lucide-react';

// Carte de concept (pour la vue grille)
const ConceptCard = ({ title, count, documents, conceptDescriptions, category, onSelect }) => {
  // Normaliser le nom du concept
  const conceptName = title; // title devrait déjà contenir le nom normalisé
  
  // Trouver la description du concept si disponible (vérifier concept et title)
  const description = conceptDescriptions.find(desc => 
    desc.concept === conceptName || desc.title === conceptName
  );
  
  // Vérifier si ce concept a un résumé défini
  const hasContext = !!(description?.resume || description?.résumé);
  
  return (
    <div 
      className="rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
      onClick={() => onSelect({ 
        title: conceptName,
        concept: conceptName,
        documents, 
        description, 
        category 
      })}
    >
      <div className="p-4">
        {/* Entête avec catégorie */}
        {category && (
          <div className="mb-2">
            <span className="inline-flex text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full">
              {category}
            </span>
          </div>
        )}
        
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-medium group-hover:text-blue-600 transition-colors">{conceptName}</h3>
        </div>
        
        {description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-2">
            {description.description || description.résumé || description.resume || description.definition || "Cliquez pour explorer ce concept"}
          </p>
        )}
        
        {!description && (
          <p className="text-sm text-gray-500 mb-2">
            Cliquez pour explorer les documents associés à ce concept
          </p>
        )}
        
        {/* Indicateurs d'info disponible */}
        <div className="flex flex-wrap gap-2 mt-2">
          {hasContext && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">
              <Tag className="h-3 w-3" />
              Résumé disponible
            </span>
          )}
          
          {description?.relatedConcepts?.length > 0 && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full border border-purple-100">
              <Network className="h-3 w-3" />
              {description.relatedConcepts.length} liaison{description.relatedConcepts.length > 1 ? 's' : ''}
            </span>
          )}
          
          {/* Ajout d'un badge simple pour indiquer le nombre de documents */}
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-gray-50 text-gray-600 rounded-full border border-gray-100">
            <FileText className="h-3 w-3" />
            {count} document{count > 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>
  );
};

// Élément de la vue en liste pour les concepts
const ConceptListItem = ({ title, count, documents, conceptDescriptions, category, onSelect }) => {
  const description = conceptDescriptions.find(desc => desc.concept === title);
  const hasContext = !!description?.resume;
  
  return (
    <div 
      className="p-3 border border-gray-200 rounded-lg bg-white hover:shadow-sm cursor-pointer group"
      onClick={() => onSelect({ title, documents, description, category })}
    >
      <div className="flex items-start gap-3">
        {/* Icône du concept */}
        <div className="mt-1 p-2 bg-blue-50 rounded-md">
          <BookOpen className="h-5 w-5 text-blue-500" />
        </div>
        
        {/* Infos principales */}
        <div className="flex-grow">
          <div className="flex items-center flex-wrap gap-2">
            <h3 className="text-lg font-medium group-hover:text-blue-600 transition-colors">{title}</h3>
            
            {category && (
              <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full">
                {category}
              </span>
            )}
            
            <span className="ml-auto px-2 py-0.5 rounded-full text-sm bg-blue-100 text-blue-700">
              {count} document{count > 1 ? 's' : ''}
            </span>
          </div>
          
          {description && (
            <p className="text-sm text-gray-600 line-clamp-2 mt-1 mb-2">
              {description.description || description.résumé || description.definition || "Cliquez pour explorer ce concept"}
            </p>
          )}
          
          {/* Badges et indicateurs */}
          <div className="flex flex-wrap gap-2">
            {hasContext && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">
                <Tag className="h-3 w-3" />
                Résumé
              </span>
            )}
            
            {description?.relatedConcepts?.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full border border-purple-100">
                <Network className="h-3 w-3" />
                {description.relatedConcepts.length} liaison{description.relatedConcepts.length > 1 ? 's' : ''}
              </span>
            )}
            
            {/* Aperçu des premiers documents */}
            {documents.slice(0, 2).map((doc, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-gray-50 text-gray-600 rounded-full">
                <FileText className="h-3 w-3" />
                {doc.metadata?.title || doc.title || `Document ${i+1}`}
              </span>
            ))}
            {documents.length > 2 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-gray-50 text-gray-600 rounded-full">
                +{documents.length - 2} autre{documents.length - 2 > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour l'affichage des concepts regroupés par catégorie
const ConceptsByCategory = ({ categories, viewMode, onSelectConcept }) => {
  return (
    <div className="space-y-6">
      {Object.entries(categories).map(([category, concepts]) => (
        <div key={category} className="space-y-3">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <PieChart className="h-5 w-5 text-indigo-500" />
            {category}
            <span className="text-sm font-normal text-gray-500">
              ({concepts.length} concept{concepts.length > 1 ? 's' : ''})
            </span>
          </h3>
          
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {concepts.map(concept => (
                <ConceptCard
                  key={concept.title}
                  {...concept}
                  onSelect={onSelectConcept}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {concepts.map(concept => (
                <ConceptListItem
                  key={concept.title}
                  {...concept}
                  onSelect={onSelectConcept}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Composant de chargement
const LoadingIndicator = () => (
  <div className="flex justify-center py-8">
    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
  </div>
);

export { ConceptCard, ConceptListItem, ConceptsByCategory, LoadingIndicator };